import React from 'react';

import type { FormInputProps } from './forms.types';
import type { InputState } from '@/types/forms';

const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      error,
      success,
      helpText,
      required = false,
      size = 'md',
      className = '',
      disabled = false,
      id,
      leftIcon,
      rightIcon,
      ...props
    },
    ref
  ) => {
    const reactId = React.useId();
    const inputId = id || `input-${reactId}`;

    // Determine state based on props
    const state: InputState = error ? 'error' : success ? 'success' : 'default';

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-body-lg md:text-body-sm min-h-9',
      md: 'px-4 py-3 text-body-lg md:text-body-sm min-h-11',
      lg: 'px-4 py-3.5 text-body-lg md:text-body-md min-h-11',
    };

    // Dark Sakura theme state classes
    const stateClasses = {
      default: 'border-stroke-default focus:border-primary-400/50',
      error: 'border-error-500 focus:border-error-500',
      success: 'border-success-500 focus:border-success-500',
    };

    const labelStateClasses = {
      default: 'text-neutral-300',
      error: 'text-error-400',
      success: 'text-success-400',
    };

    const baseClasses =
      'appearance-none block w-full border rounded-lg bg-contrast-700 text-neutral-50 placeholder:text-neutral-500 focus:outline-none focus-visible:ring-state-focus-inset transition-colors';

    const disabledClasses = disabled
      ? 'bg-contrast-700 opacity-60 cursor-not-allowed'
      : '';

    const paddingAdjustment =
      leftIcon && rightIcon
        ? 'pl-10 pr-10'
        : leftIcon
          ? 'pl-10'
          : rightIcon || state === 'error' || state === 'success'
            ? 'pr-10'
            : '';

    const classes = [
      baseClasses,
      sizeClasses[size],
      stateClasses[state],
      disabledClasses,
      paddingAdjustment,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Determine message to display
    const message = error || success || helpText;
    const messageId = `${inputId}-message`;

    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className={`block text-body-sm font-medium ${labelStateClasses[state]}`}
          >
            {label}
            {required && <span className="text-primary-400 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-neutral-500">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            className={classes}
            disabled={disabled}
            required={required}
            aria-invalid={state === 'error'}
            aria-describedby={message ? messageId : undefined}
            {...props}
          />

          {/* State icons or custom right icon */}
          {(rightIcon || state === 'error' || state === 'success') && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              {state === 'error' ? (
                <svg
                  className="h-5 w-5 text-error-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : state === 'success' ? (
                <svg
                  className="h-5 w-5 text-success-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <span className="text-neutral-500">{rightIcon}</span>
              )}
            </div>
          )}
        </div>

        {message && (
          <p
            id={messageId}
            className={`text-label-xs ${
              error
                ? 'text-error-400'
                : success
                  ? 'text-success-400'
                  : 'text-neutral-500'
            }`}
          >
            {message}
          </p>
        )}
      </div>
    );
  }
);

FormInput.displayName = 'FormInput';

export default FormInput;
